import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@material-ui/core';
import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank } from '@material-ui/icons';
import { DialogWidth, SortingCenterUserRole, User, UserRole } from 'types';
import ClosureErrorIcon from 'components/Icons/ClosureErrorIcon';
import NewDialog from 'components/NewDialog';
import { putAdministrationSortingCenterUserRoleEdit } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { ValidateIcon } from 'components/Icons';

const useStyles = makeStyles(theme => ({
  content: {
    marginBottom: '32px',
    marginRight: '32px',

    '& p': {
      fontSize: 14,
      lineHeight: '16px',
      textAlign: 'left',
      verticalAlign: 'top',
      fontWeight: 600,
      color: '#0F626F',
    },
    '& p span': {
      color: '#148192',
    },
  },
  bottomButton: {
    textAlign: 'right',
  },
  closeIcon: {
    width: '16px',
  },
  continueIcon: {
    width: '16px',
  },
  continueButton: {
    backgroundColor: '#2F3741',
    color: '#FFFFFF',
    marginLeft: theme.spacing(2),

    '&:hover': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
    '&:active': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
    '&:disabled': {
      backgroundColor: '#CFD4DA',
      color: '#FFFFFF',
      '& *': {
        color: '#FFFFFF',
      },
    },
  },

  contentForm: {
    width: '100%',
    justifyContent: 'center',
  },
  contentFormDiv: {
    alignSelf: 'center',
    width: '40%',
  },

  radioGroup: {
    width: '100%',

    '& label': {
      width: '100%',
      justifyContent: 'left',
    },
    '& span': {
      fontSize: '14px',
      fontWeight: 400,
    },
    '& label span:last-Child': {
      width: '65%',
    },
  },
}));

interface Props {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  sortingCenterUserRole?: SortingCenterUserRole | null;
  handleCancel?: () => void;
  handleContinue?: () => void;

  user: User;
  formName?: string;
}

export const DialogSortingCenterUserRolesEdit: React.FC<Props> = ({ isOpen, setOpen, sortingCenterUserRole, user }) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.sortingCenterUserRoleEdit' });

  const sortingCenter = sortingCenterUserRole?.sortingCenter;

  const handleCancel = () => {
    setCurrentUserRole(undefined);
    setIsInitialState(true);
    setOpen(false);
  };

  const handleContinue = () => {
    if (isInitialState) return;

    return putAdministrationSortingCenterUserRoleEdit(sortingCenterUserRole, user, currentUserRole)
      .then(() => {
        setOpen(false);
        notify({
          message: t('success'),
          severity: 'success',
        });
      })
      .catch(() => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  const roleInitial = sortingCenterUserRole?.role;
  const [currentUserRole, setCurrentUserRole] = useState<UserRole | undefined>(roleInitial);
  const [isInitialState, setIsInitialState] = useState<boolean>(true);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const forUserRole = (event.target as HTMLInputElement).value as UserRole;
    setCurrentUserRole(forUserRole);
    setIsInitialState(!Boolean(forUserRole !== roleInitial));
  };

  return (
    <NewDialog
      closable={true}
      isOpen={isOpen}
      onClose={handleCancel}
      width={DialogWidth.LARGE_FORM}
      title={t('title')}
      renderContent={() => (
        <div className={classes.content}>
          {sortingCenter && (
            <Typography>
              {sortingCenter.name} - <span>{sortingCenter.code}</span>
            </Typography>
          )}
          <br />
          <FormControl className={classes.contentForm}>
            <div className={classes.contentFormDiv}>
              <RadioGroup
                name={'role'}
                className={classes.radioGroup}
                value={currentUserRole || roleInitial}
                onChange={handleChange}
              >
                <FormControlLabel
                  label={'Responsable'}
                  value={UserRole.ROLE_MANAGER}
                  labelPlacement="start"
                  control={<Radio color={'primary'} checkedIcon={<CheckBoxIcon />} icon={<CheckBoxOutlineBlank />} />}
                />
                <FormControlLabel
                  label={'Saisie'}
                  value={UserRole.ROLE_ASSISTANT}
                  labelPlacement="start"
                  control={<Radio color={'primary'} checkedIcon={<CheckBoxIcon />} icon={<CheckBoxOutlineBlank />} />}
                />
                <FormControlLabel
                  label={'Consultation'}
                  value={UserRole.ROLE_CONSULTATION}
                  labelPlacement="start"
                  control={<Radio color={'primary'} checkedIcon={<CheckBoxIcon />} icon={<CheckBoxOutlineBlank />} />}
                />
              </RadioGroup>
            </div>
          </FormControl>
        </div>
      )}
      renderFooter={() => (
        <>
          <div className={classes.bottomButton}>
            <Button
              variant="outlined"
              startIcon={<ClosureErrorIcon className={classes.closeIcon} />}
              onClick={handleCancel}
            >
              {t('cancel')}
            </Button>
            <Button
              className={classes.continueButton}
              variant="outlined"
              disabled={isInitialState}
              endIcon={<ValidateIcon className={classes.continueIcon} />}
              onClick={handleContinue}
            >
              {t('continue')}
            </Button>
          </div>
        </>
      )}
    />
  );
};
